import React, { useContext } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { ApplicationContext } from '../../context/GlobalState';
import { t, extractLocaleFromPath } from '../../i18n';
import HreflangTags from '../../components/HreflangTags/HreflangTags';
import mediaData from '../../data/media/media-content.json';
import './Videos.css';

export default function Videos() {
  const { state } = useContext(ApplicationContext);
  const location = useLocation();
  const { locale } = extractLocaleFromPath(location.pathname);
  const brand = state.brand;
  const brandKey = brand?.brandKey || 'gso';
  const brandName = brand?.name || 'Our Practice';
  const baseDomain = brand?.domain || state.client?.domain || 'gaspineortho.com';

  const filteredVideos = (mediaData.youtube || []).filter(
    v => v?.brand === 'all' || v?.brand === brandKey
  );

  const showViewMedica = brandKey === 'gso';

  const canonicalUrl = `https://${baseDomain}/videos`;
  const pageTitle = t('videos.pageTitle', locale).replace('{{brandName}}', brandName);
  const pageDesc = t('videos.metaDesc', locale).replace('{{brandName}}', brandName);
  const ogImage = `https://${baseDomain}/assets/brand/${brandKey}/logo-color.webp`;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: pageTitle,
    description: pageDesc,
    url: canonicalUrl,
    itemListElement: filteredVideos.map((v, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      item: {
        '@type': 'VideoObject',
        name: v.title?.[locale] || v.title?.en || '',
        embedUrl: `https://www.youtube-nocookie.com/embed/${v.id}`,
        thumbnailUrl: `https://img.youtube.com/vi/${v.id}/hqdefault.jpg`,
        uploadDate: v.uploadDate || '2024-01-01',
      },
    })),
  };

  return (
    <div className="videos-page">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDesc} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={ogImage} />
        <link rel="canonical" href={canonicalUrl} />
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>
      <HreflangTags baseDomain={baseDomain} pathWithoutLocale="/videos" />

      {/* Hero */}
      <section className="videos-hero glass-dark">
        <div className="container">
          <h1>{t('videos.heroTitle', locale)}</h1>
          <p>{brandName}</p>
        </div>
      </section>

      {/* ViewMedica — GSO only */}
      {showViewMedica && (
        <section className="section-padding">
          <div className="container videos-container">
            <h2 className="section-title">{t('media.patientEd', locale)}</h2>
            <p className="section-subtitle">{t('media.patientEdDesc', locale)}</p>
            <div className="glass-card videos-viewmedica">
              <div className="video-embed-lg">
                <iframe
                  src={mediaData.viewmedica?.embedUrl}
                  title={t('media.patientEd', locale)}
                  frameBorder="0"
                  allowFullScreen
                  loading="lazy"
                  style={{ border: 'none' }}
                />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* YouTube Videos — single column, larger */}
      <section className="section-padding">
        <div className="container videos-container">
          <h2 className="section-title">{t('media.videoPlaylist', locale)}</h2>
          <p className="section-subtitle">{t('media.videoPlaylistDesc', locale)}</p>
          <div className="videos-list">
            {filteredVideos.map((video) => (
              <div key={video.id} className="videos-item glass-card">
                <div className="video-embed-lg">
                  <iframe
                    src={`https://www.youtube-nocookie.com/embed/${video.id}`}
                    title={video.title?.[locale] || video.title?.en || ''}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    loading="lazy"
                  />
                </div>
                <div className="videos-item-info">
                  <h3>{video.title?.[locale] || video.title?.en || ''}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
